import React, { useState } from "react";
import { Layout, Menu, Breadcrumb } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Content, Sider } = Layout;

const graphs = [
  {
    id: "graph1",
    title: "Evolución en el tiempo",
    menuTitle: "Evolución en el tiempo",
    text: `En 2024 se produjo un cambio de tendencia: por primera vez desde 2015, la proporción de niños y niñas que residen en sectores con violencia crítica experimentó un 
          descenso. Tras alcanzar un 47% en 2022 —cifra que refleja la exposición a balaceras o peleas frecuentes—, la prevalencia se redujo de manera estadísticamente 
          significativa a un 44% en 2024.`,
    link: "https://public.flourish.studio/visualisation/20935846/embed",
  },
  {
    id: "graph2",
    title: "Análisis por sexo",
    menuTitle: "\u2022 Sexo",
    text: `Según este indicador, que mide la calidad del barrio a nivel de hogar, niñas y niños están igualmente expuestos a vivir en barrios con violencia crítica. Esto no 
          implica que tengan las mismas probabilidades de experimentar o presenciar violencia, ya que el indicador no mide las experiencias individuales de niñas y niños, 
          sino que recoge la percepción de al menos un miembro del hogar.`,
    link: "https://public.flourish.studio/visualisation/20935857/embed",
  },
  {
    id: "graph3",
    title: "Análisis por edad",
    menuTitle: "\u2022 Edad",
    text: `Niñas y niños de distintos tramos etarios viven en barrios con violencia crítica en proporciones similares: el 42% de las niñas y los niños en su primera infancia, 
          el 44% de aquellos en su infancia media y 45% en la adolescencia. Estas diferencias no son estadísticamente significativas.`,
    link: "https://public.flourish.studio/visualisation/20935860/embed",
  },
  {
    id: "graph4",
    title: "Análisis por país de nacimiento",
    menuTitle: "\u2022 País de nacimiento",
    text: `No existen diferencias significativas entre la proporción de niñas y niños nacidos en Chile y nacidos fuera del país que viven en barrios con violencia crítica.`,
    link: "https://public.flourish.studio/visualisation/20935864/embed",
  },
  {
    id: "graph6",
    title: "Análisis por pueblo originario",
    menuTitle: "\u2022 Pueblo originario",
    text: `No existen diferencias significativas en la proporción de niñas y niños que viven en barrios con violencia crítica entre aquellos que provienen de hogares que se 
          identifican con pueblos originarios y aquellos que no.`,
    link: "https://public.flourish.studio/visualisation/20935877/embed",
  },
  {
    id: "graph7",
    title: "Análisis por quintil de ingresos",
    menuTitle: "\u2022 Quintil de ingresos",
    text: `En toda la serie de tiempo se mantiene una tendencia constante: la exposición a la violencia crítica en los barrios está fuertemente condicionada por el nivel de 
          ingresos. En 2024, la mitad de las niñas y niños pertenecientes a los dos primeros quintiles residen en entornos con alta violencia. Si bien a partir del tercer quintil 
          se observa una disminución, el descenso más pronunciado ocurre en los estratos de mayores ingresos. De hecho, en el quinto quintil la prevalencia es del 24%, cifra 
          que representa casi la mitad de lo registrado en los sectores más vulnerables.`,
    link: "https://public.flourish.studio/visualisation/20935880/embed",
  },
  {
    id: "graph5",
    title: "Análisis por zona de residencia",
    menuTitle: "\u2022 Zona",
    text: `Las niñas y niños de zonas urbanas están más expuestos a vivir en barrios con violencia crítica. En 2024, 1 de cada 2 niñas y niños (47%) de zonas urbanas vive en 
          barrios con violencia crítica, mientras que en zonas rurales la proporción es de 1 de cada 10 (13%). Respecto a 2022, solo en zonas urbanas disminuyó la exposición a 
          la violencia crítica de niñas y niños, mientras que en zonas rurales la proporción se mantuvo constante.`,
    link: "https://public.flourish.studio/visualisation/20935871/embed",
  },
  {
    id: "graph8",
    title: "Análisis por región",
    menuTitle: "\u2022 Región",
    text: `La proporción de niñas y niños que viven en barrios con violencia crítica varía significativamente según la región. La región Metropolitana concentra la mayor 
          proporción de niñas y niños en barrios con estas características (56%), seguido por la región del Biobío (46%) y la de Valparaíso (42%). Respecto a 2022, en ninguna 
          de estas tres regiones disminuyó la proporción de niñas y niños con violencia crítica en sus barrios. 

  En el extremo opuesto, la región de Magallanes exhibe la prevalencia más baja del país con un 8%. Este dato destaca por la amplia brecha respecto al resto del territorio: 
          existe una diferencia de 18 puntos porcentuales con la región de La Araucanía, que se sitúa como el segundo territorio con menor prevalencia de violencia.`,
    link: "https://public.flourish.studio/visualisation/20935850/embed",
  }
];


const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("graph1");

  const handleMenuItemClick = (graphId, graphTitle) => {
    setSelectedMenuItem(graphId);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const selectedGraph = graphs.find((graph) => graph.id === selectedMenuItem);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={250}
        collapsible
        collapsed={collapsed}
        onCollapse={toggleCollapsed}
        style={{ background: "#fff" }}
        breakpoint="lg"
        collapsedWidth={0}
      >
        <Menu theme="dark" mode="vertical" selectedKeys={[selectedMenuItem]} style={{ background: "#fff" }}>
          {graphs.map((graph) => (
            <Menu.Item
              key={graph.id}
              onClick={() => handleMenuItemClick(graph.id)}
              style={{ color: 'black', paddingLeft: graph.id !== "graph1" ? "30px" : "20px" }}
            >
              {graph.menuTitle}
            </Menu.Item>
          ))}
        </Menu>
      </Sider>
      <Layout style={{ padding: "24px 24px 24px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item></Breadcrumb.Item>
        </Breadcrumb>
        <Content
          id="content"
          style={{
            background: "#ffff",
            padding: 24,
            margin: 0,
            minHeight: 280,
          }}
        >
          {selectedGraph && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h1>{selectedGraph.title}</h1>
              </div>
              <div style={{ textAlign: 'justify' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedGraph.text}
                </ReactMarkdown>
              </div>
              <iframe
                src={selectedGraph.link}
                frameBorder="0"
                scrolling="no"
                style={{ minWidth: "100%", height: "500px" }}
              ></iframe>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;
